import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    SelectChangeEvent,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Chip,
    Tooltip,
    CircularProgress
} from '@mui/material';
import ResponsiveModal from './ResponsiveModal';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    DragIndicator as DragIcon,
    ArrowUpward as ArrowUpIcon,
    ArrowDownward as ArrowDownIcon,
    Refresh as RefreshIcon,
    LibraryBooks as LibraryIcon,
    BookmarkAdd as BookmarkAddIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import { FieldDefinition, FieldOption, DynamicSource, PriceOption, CapacityOption } from '../../services/accountTypeService';
import fieldTemplateService, { FieldTemplate } from '../../services/fieldTemplateService';
import metadataService, { FieldInfo, CollectionInfo } from '../../services/metadataService';
import { useSnackbar } from 'notistack';

interface FieldEditorProps {
    fields: FieldDefinition[];
    onChange: (fields: FieldDefinition[]) => void;
}

const FieldEditor: React.FC<FieldEditorProps> = ({ fields, onChange }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentField, setCurrentField] = useState<Partial<FieldDefinition> | null>(null);
    const [currentIndex, setCurrentIndex] = useState<number | null>(null);
    const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
    const [collectionFields, setCollectionFields] = useState<FieldInfo[]>([]);
    const [loadingFields, setLoadingFields] = useState<boolean>(false);
    const [availableCollections, setAvailableCollections] = useState<CollectionInfo[]>([]);
    const [loadingCollections, setLoadingCollections] = useState<boolean>(false);
    // Field types available to select from
    const fieldTypes = [
        { value: 'text', label: 'متن' },
        { value: 'number', label: 'عدد' },
        { value: 'email', label: 'ایمیل' },
        { value: 'date', label: 'تاریخ' },
        { value: 'dropdown', label: 'لیست کشویی' },
        { value: 'checkbox', label: 'چک باکس' },
        { value: 'radio', label: 'دکمه رادیویی' },
        { value: 'price', label: 'قیمت' },
        { value: 'inventory', label: 'موجودی' },
        { value: 'textarea', label: 'متن چند خطی' },
        { value: 'password', label: 'رمز عبور' },
        { value: 'capacity_type', label: 'حجم / سایز (چند گزینه‌ای با قیمت جدا)' },
        { value: 'value', label: 'مقدار' },
        { value: 'color', label: 'رنگ (Color Picker)' },
    ];

    // Load collections from backend when component mounts
    useEffect(() => {
        loadCollections();
    }, []);

    // Load available collections from backend
    const loadCollections = async () => {
        try {
            setLoadingCollections(true);
            const collections = await metadataService.getCollections();
            setAvailableCollections(collections);
        } catch (error) {
            console.error('Error loading collections:', error);
            enqueueSnackbar('خطا در بارگذاری لیست کالکشن‌ها', { variant: 'error' });
            setAvailableCollections([]);
        } finally {
            setLoadingCollections(false);
        }
    };

    // بارگذاری فیلدهای کالکشن انتخاب شده
    const loadCollectionFields = async (collectionId: string) => {
        if (!collectionId) return;

        try {
            setLoadingFields(true);
            const fields = await metadataService.getCollectionFields(collectionId);
            setCollectionFields(fields);
        } catch (error) {
            console.error(`Error loading fields for collection ${collectionId}:`, error);
            enqueueSnackbar(`خطا در بارگذاری فیلدهای ${collectionId}`, { variant: 'error' });
            setCollectionFields([]);
        } finally {
            setLoadingFields(false);
        }
    };

    // وقتی کالکشن تغییر می‌کند، فیلدها را بارگذاری کن
    useEffect(() => {
        if (currentField?.options?.dynamicSource?.collectionName) {
            const options = currentField.options!;
            const dynamicSource = options.dynamicSource!;
            loadCollectionFields(dynamicSource.collectionName);
        }
    }, [currentField?.options?.dynamicSource?.collectionName]);

    // Open dialog to create a new field
    const handleAddField = () => {
        setCurrentField({
            type: 'text',
            name: '',
            label: '',
            placeholder: '',
            description: '',
            required: false,
            order: fields.length,
            options: {
                isStatic: true,
                items: [],
                isMultiSelect: false
            }
        });
        setCurrentIndex(null);
        setDialogMode('create');
        setDialogOpen(true);
    };

    // Open dialog to edit existing field
    const handleEditField = (index: number) => {
        setCurrentField({ ...fields[index] });
        setCurrentIndex(index);
        setDialogMode('edit');
        setDialogOpen(true);

        // اگر فیلد دارای منبع پویا است، فیلدهای آن را بارگذاری کن
        if (fields[index].options?.dynamicSource?.collectionName) {
            const options = fields[index].options!;
            const dynamicSource = options.dynamicSource!;
            loadCollectionFields(dynamicSource.collectionName);
        }
    };

    // Delete a field
    const handleDeleteField = (index: number) => {
        const newFields = [...fields];
        newFields.splice(index, 1);

        // Update order of remaining fields
        for (let i = 0; i < newFields.length; i++) {
            newFields[i].order = i;
        }

        onChange(newFields);
    };

    // Move field up in the order
    const handleMoveUp = (index: number) => {
        if (index === 0) return;

        const newFields = [...fields];
        const temp = newFields[index];
        newFields[index] = newFields[index - 1];
        newFields[index - 1] = temp;

        // Update order
        for (let i = 0; i < newFields.length; i++) {
            newFields[i].order = i;
        }

        onChange(newFields);
    };

    // Move field down in the order
    const handleMoveDown = (index: number) => {
        if (index === fields.length - 1) return;

        const newFields = [...fields];
        const temp = newFields[index];
        newFields[index] = newFields[index + 1];
        newFields[index + 1] = temp;

        // Update order
        for (let i = 0; i < newFields.length; i++) {
            newFields[i].order = i;
        }

        onChange(newFields);
    };

    // Handle closing the dialog
    const handleCloseDialog = () => {
        setDialogOpen(false);
        setCurrentField(null);
        setCollectionFields([]);
    };

    // تابع جدید برای تولید نام فیلد از روی عنوان نمایشی
    const generateFieldName = () => {
        if (!currentField || !currentField.label) return;
        
        // تبدیل عنوان نمایشی به یک نام فیلد معتبر
        const generatedName = currentField.label
            .toLowerCase()
            .replace(/\s+/g, '_')     // تبدیل فاصله به زیرخط
            .replace(/[^a-zA-Z0-9_]/g, '') // حذف کاراکترهای غیرمجاز
            .substring(0, 30);        // محدود کردن طول نام
        
        if (generatedName) {
            setCurrentField({
                ...currentField,
                name: generatedName
            });
        }
    };

    // Handle form field changes
    const handleFieldChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
    ) => {
        const { name, value } = e.target;
        const checked = 'checked' in e.target ? e.target.checked : undefined;

        if (!currentField || !name) return;

        // برای فیلد "name"، فقط حروف انگلیسی، اعداد و زیرخط را قبول کن
        if (name === 'name') {
            // فیلتر کردن کاراکترهای غیرمجاز
            const filteredValue = value.replace(/[^a-zA-Z0-9_]/g, '');
            
            setCurrentField({
                ...currentField,
                [name]: filteredValue
            });
            return;
        }

        // تولید خودکار نام فیلد از روی عنوان نمایشی 
        if (name === 'label' && (!currentField.name || currentField.name === '')) {
            // تبدیل عنوان نمایشی به یک نام فیلد معتبر
            const generatedName = value
                .toLowerCase()
                .replace(/\s+/g, '_')     // تبدیل فاصله به زیرخط
                .replace(/[^a-zA-Z0-9_]/g, '') // حذف کاراکترهای غیرمجاز
                .substring(0, 30);        // محدود کردن طول نام
            
            setCurrentField({
                ...currentField,
                [name]: value,
                name: generatedName
            });
            return;
        }

        if (name === 'required' && checked !== undefined) {
            setCurrentField({
                ...currentField,
                [name]: checked
            });
        } else if (name === 'isStatic' && checked !== undefined) {
            setCurrentField({
                ...currentField,
                options: {
                    ...currentField.options,
                    isStatic: checked
                }
            });
        } else if (name === 'isMultiSelect' && checked !== undefined) {
            setCurrentField({
                ...currentField,
                options: {
                    ...currentField.options,
                    isMultiSelect: checked
                }
            });
        } else {
            // اگر نوع فیلد تغییر کرد
            if (name === 'type') {
                const newField = {
                    ...currentField,
                    [name]: value as FieldDefinition['type']
                };

                // اگر فیلد capacity_type شد و capacityTypes وجود ندارد، آن را initialize کن
                if (value === 'capacity_type' && (!newField.options?.capacityTypes || newField.options.capacityTypes.length === 0)) {
                    newField.options = {
                        ...newField.options,
                        capacityTypes: []
                    };
                }

                setCurrentField(newField);
        } else {
            setCurrentField({
                ...currentField,
                [name]: value
            });
            }
        }
    };

    // Handle dropdown option changes
    const handleOptionChange = (index: number, field: keyof FieldOption, value: string) => {
        if (!currentField || !currentField.options || !currentField.options.items) return;

        const newOptions = [...currentField.options.items];
        newOptions[index] = {
            ...newOptions[index],
            [field]: value
        };

        setCurrentField({
            ...currentField,
            options: {
                ...currentField.options,
                items: newOptions
            }
        });
    };

    // Add new dropdown option
    const handleAddOption = () => {
        if (!currentField || !currentField.options) return;

        const newOptions = currentField.options.items ? [...currentField.options.items] : [];
        newOptions.push({ label: '', value: '' });

        setCurrentField({
            ...currentField,
            options: {
                ...currentField.options,
                items: newOptions
            }
        });
    };

    // Delete dropdown option
    const handleDeleteOption = (index: number) => {
        if (!currentField || !currentField.options || !currentField.options.items) return;

        const newOptions = [...currentField.options.items];
        newOptions.splice(index, 1);

        setCurrentField({
            ...currentField,
            options: {
                ...currentField.options,
                items: newOptions
            }
        });
    };

    // Handle dynamic source changes
    const handleDynamicSourceChange = (field: keyof DynamicSource, value: string) => {
        if (!currentField || !currentField.options) return;

        const dynamicSource = currentField.options.dynamicSource || {
            collectionName: '',
            displayField: '',
            valueField: '',
            isMultiSelect: false
        };

        setCurrentField({
            ...currentField,
            options: {
                ...currentField.options,
                dynamicSource: {
                    ...dynamicSource,
                    [field]: value
                }
            }
        });
    };

    // Handle price option changes
    const handlePriceOptionChange = (index: number, field: keyof PriceOption, value: string | number) => {
        if (!currentField || !currentField.options || !currentField.options.priceMatrix) return;

        const newPriceMatrix = [...currentField.options.priceMatrix];
        newPriceMatrix[index] = {
            ...newPriceMatrix[index],
            [field]: field === 'description' ? value : Number(value)
        };

        setCurrentField({
            ...currentField,
            options: {
                ...currentField.options,
                priceMatrix: newPriceMatrix
            }
        });
    };

    // Add new price option
    const handleAddPriceOption = () => {
        if (!currentField || !currentField.options) return;

        const newPriceMatrix = currentField.options.priceMatrix ? [...currentField.options.priceMatrix] : [];
        newPriceMatrix.push({ capacity: 0, price: 0 });

        setCurrentField({
            ...currentField,
            options: {
                ...currentField.options,
                priceMatrix: newPriceMatrix
            }
        });
    };

    // Delete price option
    const handleDeletePriceOption = (index: number) => {
        if (!currentField || !currentField.options || !currentField.options.priceMatrix) return;

        const newPriceMatrix = [...currentField.options.priceMatrix];
        newPriceMatrix.splice(index, 1);

        setCurrentField({
            ...currentField,
            options: {
                ...currentField.options,
                priceMatrix: newPriceMatrix
            }
        });
    };

    // Handle capacity option changes
    const handleCapacityOptionChange = (index: number, field: keyof CapacityOption, value: string | number | string[]) => {
        if (!currentField || !currentField.options || !currentField.options.capacityTypes) return;

        const newCapacityTypes = [...currentField.options.capacityTypes];
        newCapacityTypes[index] = {
            ...newCapacityTypes[index],
            [field]: value as string | string[]
        };

        setCurrentField({
            ...currentField,
            options: {
                ...currentField.options,
                capacityTypes: newCapacityTypes
            }
        });
    };

    // Add new capacity option
    const handleAddCapacityOption = () => {
        if (!currentField || !currentField.options) return;

        const newCapacityTypes = currentField.options.capacityTypes ? [...currentField.options.capacityTypes] : [];
        const newIndex = newCapacityTypes.length + 1;
        const newCapacityName = `حجم / سایز ${newIndex}`;
        
        newCapacityTypes.push({ 
            name: newCapacityName,
            value: newCapacityName, // پیش‌فرض value همان name باشد
            description: `توضیحات ${newCapacityName}`
        });

        setCurrentField({
            ...currentField,
            options: {
                ...currentField.options,
                capacityTypes: newCapacityTypes
            }
        });
    };

    // Delete capacity option
    const handleDeleteCapacityOption = (index: number) => {
        if (!currentField || !currentField.options || !currentField.options.capacityTypes) return;

        const newCapacityTypes = [...currentField.options.capacityTypes];
        newCapacityTypes.splice(index, 1);

        setCurrentField({
            ...currentField,
            options: {
                ...currentField.options,
                capacityTypes: newCapacityTypes
            }
        });
    };

    // ─── Visibility (Conditional rendering) handlers ──────────────────────────
    const handleToggleVisibility = (enabled: boolean) => {
        if (!currentField) return;
        if (enabled) {
            setCurrentField({
                ...currentField,
                visibility: currentField.visibility ?? { conditions: [{ fieldName: '', operator: 'truthy' }], logic: 'AND' }
            });
        } else {
            const next = { ...currentField } as any;
            delete next.visibility;
            setCurrentField(next);
        }
    };

    const handleAddVisibilityCondition = () => {
        if (!currentField) return;
        const v = currentField.visibility || { conditions: [], logic: 'AND' as const };
        setCurrentField({
            ...currentField,
            visibility: { ...v, conditions: [...v.conditions, { fieldName: '', operator: 'truthy' }] }
        });
    };

    const handleRemoveVisibilityCondition = (index: number) => {
        if (!currentField?.visibility) return;
        const conditions = [...currentField.visibility.conditions];
        conditions.splice(index, 1);
        setCurrentField({ ...currentField, visibility: { ...currentField.visibility, conditions } });
    };

    const handleVisibilityConditionChange = (index: number, key: 'fieldName' | 'operator' | 'value', value: any) => {
        if (!currentField?.visibility) return;
        const conditions = currentField.visibility.conditions.map((c, i) => i === index ? { ...c, [key]: value } : c);
        setCurrentField({ ...currentField, visibility: { ...currentField.visibility, conditions } });
    };

    const handleVisibilityLogicChange = (logic: 'AND' | 'OR') => {
        if (!currentField?.visibility) return;
        setCurrentField({ ...currentField, visibility: { ...currentField.visibility, logic } });
    };

    // Handle save field
    const handleSaveField = () => {
        if (!currentField || !currentField.name || !currentField.label || !currentField.type) return;

        // نیازی به بررسی regex نیست چون قبلاً کاراکترهای غیرمجاز را فیلتر کرده‌ایم
        // اگر فیلد خالی باشد، فقط بررسی می‌کنیم
        if (currentField.name.length === 0) {
            alert('لطفاً یک نام برای فیلد وارد کنید');
            return;
        }

        // Validate dropdown fields have options
        if (currentField.type === 'dropdown' || currentField.type === 'radio') {
            if (currentField.options?.isStatic) {
                if (!currentField.options?.items || currentField.options.items.length === 0) {
                    alert('فیلدهای لیست کشویی یا رادیویی باید حداقل یک گزینه داشته باشند');
                    return;
                }
            } else {
                if (!currentField.options?.dynamicSource?.collectionName ||
                    !currentField.options.dynamicSource.displayField ||
                    !currentField.options.dynamicSource.valueField) {
                    alert('منبع پویا باید کالکشن، فیلد نمایش و فیلد مقدار را مشخص کند');
                    return;
                }
            }
        }

        // Validate price fields
        if (currentField.type === 'price') {
            if (!currentField.options?.priceMatrix || currentField.options.priceMatrix.length === 0) {
                alert('فیلدهای قیمت باید حداقل یک گزینه قیمت داشته باشند');
                return;
            }
        }

        // Validate capacity type fields
        if (currentField.type === 'capacity_type') {
            if (!currentField.options?.capacityTypes || currentField.options.capacityTypes.length === 0) {
                alert('فیلدهای حجم / سایز باید حداقل یک گزینه داشته باشند');
                return;
            }
        }

        // Prepare the field
        const fieldData: FieldDefinition = {
            ...currentField as FieldDefinition
        };

        // Drop the option sub-objects that belong to other field types, but KEEP
        // the type-agnostic flags. Deleting `options` wholesale used to destroy
        // `moneyFormat`/`currencyField` on number fields (and any future flag)
        // the moment an admin opened and re-saved the field.
        const OPTION_OWNING_TYPES = ['dropdown', 'radio', 'price', 'capacity_type', 'credentials'];
        if (!OPTION_OWNING_TYPES.includes(fieldData.type) && fieldData.options) {
            const { items, dynamicSource, priceMatrix, capacityTypes, credentials, isStatic, isMultiSelect, ...keep } =
                fieldData.options as any;
            // `keep` holds moneyFormat, currencyField, serviceProduct,
            // perCapacityInventory … — anything not tied to a picker UI.
            const meaningful = Object.entries(keep).filter(
                ([, v]) => v !== undefined && v !== null && v !== false && v !== ''
            );
            if (meaningful.length > 0) {
                fieldData.options = Object.fromEntries(meaningful) as any;
            } else {
                delete fieldData.options;
            }
        }

        // Save field to the list
        const newFields = [...fields];
        if (dialogMode === 'edit' && currentIndex !== null) {
            newFields[currentIndex] = fieldData;
        } else {
            newFields.push(fieldData);
        }

        // Update order of all fields
        for (let i = 0; i < newFields.length; i++) {
            newFields[i].order = i;
        }

        onChange(newFields);
        handleCloseDialog();
    };

    // ─── Field Templates (قالب‌های فیلد) ────────────────────────────────────
    const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
    const [templates, setTemplates] = useState<FieldTemplate[]>([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [templateSearch, setTemplateSearch] = useState('');
    const [templateCategoryFilter, setTemplateCategoryFilter] = useState<string>('');
    const [templateCategories, setTemplateCategories] = useState<string[]>([]);

    const [saveAsTemplateOpen, setSaveAsTemplateOpen] = useState(false);
    const [templateSaveName, setTemplateSaveName] = useState('');
    const [templateSaveDescription, setTemplateSaveDescription] = useState('');
    const [templateSaveCategory, setTemplateSaveCategory] = useState('');
    const [templateSaving, setTemplateSaving] = useState(false);

    // deep clone یک تعریف فیلد - برای copy-on-insert
    const deepClone = <T,>(obj: T): T => {
        try {
            // @ts-ignore — structuredClone موجود در مرورگرهای مدرن
            if (typeof structuredClone === 'function') return structuredClone(obj);
        } catch { /* fallback */ }
        return JSON.parse(JSON.stringify(obj));
    };

    const loadTemplates = async () => {
        try {
            setTemplatesLoading(true);
            const [list, cats] = await Promise.all([
                fieldTemplateService.list({
                    search: templateSearch || undefined,
                    category: templateCategoryFilter || undefined,
                    limit: 200,
                }),
                fieldTemplateService.getCategories(),
            ]);
            setTemplates(list.data || []);
            setTemplateCategories(cats || []);
        } catch (e) {
            console.error('Error loading templates:', e);
            enqueueSnackbar('خطا در دریافت قالب‌ها', { variant: 'error' });
        } finally {
            setTemplatesLoading(false);
        }
    };

    const handleOpenTemplatePicker = async () => {
        setTemplatePickerOpen(true);
        await loadTemplates();
    };

    const handleInsertTemplate = async (tpl: FieldTemplate) => {
        try {
            // افزایش usageCount در سرور (silent)
            fieldTemplateService.use(tpl._id).catch(() => undefined);

            // یک کپی عمیق از تعریف فیلد - تغییرات بعدی روی قالب اصلی اثر نمی‌گذارد
            const cloned: any = deepClone(tpl.fieldDefinition);

            // اگر فیلدی با همین name وجود دارد، یک suffix عددی اضافه کن
            let baseName = String(cloned.name || 'field').replace(/[^a-zA-Z0-9_]/g, '_');
            let candidateName = baseName;
            let i = 2;
            const existingNames = new Set(fields.map(f => f.name));
            while (existingNames.has(candidateName)) {
                candidateName = `${baseName}_${i++}`;
            }
            cloned.name = candidateName;
            cloned.order = fields.length;

            onChange([...fields, cloned]);
            enqueueSnackbar(`قالب «${tpl.name}» اضافه شد`, { variant: 'success' });
            setTemplatePickerOpen(false);
        } catch (e: any) {
            enqueueSnackbar(e?.message || 'خطا در درج قالب', { variant: 'error' });
        }
    };

    const handleOpenSaveAsTemplate = () => {
        if (!currentField || !currentField.name || !currentField.label) {
            enqueueSnackbar('ابتدا فیلد را تکمیل کنید', { variant: 'warning' });
            return;
        }
        setTemplateSaveName(currentField.label || '');
        setTemplateSaveDescription('');
        setTemplateSaveCategory('');
        setSaveAsTemplateOpen(true);
    };

    const handleSaveAsTemplate = async () => {
        if (!currentField) return;
        const name = templateSaveName.trim();
        if (!name) {
            enqueueSnackbar('نام قالب الزامی است', { variant: 'warning' });
            return;
        }
        try {
            setTemplateSaving(true);
            await fieldTemplateService.create({
                name,
                description: templateSaveDescription.trim() || undefined,
                category: templateSaveCategory.trim() || undefined,
                fieldDefinition: deepClone(currentField as FieldDefinition),
            });
            enqueueSnackbar('قالب با موفقیت ذخیره شد', { variant: 'success' });
            setSaveAsTemplateOpen(false);
        } catch (e: any) {
            const msg = e?.response?.data?.message || e?.message || 'خطا در ذخیره قالب';
            enqueueSnackbar(msg, { variant: 'error' });
        } finally {
            setTemplateSaving(false);
        }
    };

    // Render the field editor
    return (
        <Box dir="rtl">
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle1">فیلدها</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<LibraryIcon />}
                        onClick={handleOpenTemplatePicker}
                    >
                        افزودن از قالب
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleAddField}
                    >
                        افزودن فیلد
                    </Button>
                </Box>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell width="5%">ترتیب</TableCell>
                            <TableCell width="15%">نام</TableCell>
                            <TableCell width="15%">عنوان</TableCell>
                            <TableCell width="15%">نوع</TableCell>
                            <TableCell width="10%">اجباری</TableCell>
                            <TableCell width="25%">توضیحات</TableCell>
                            <TableCell width="15%">عملیات</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {fields.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    هیچ فیلدی تعریف نشده است. برای ایجاد فیلد جدید روی "افزودن فیلد" کلیک کنید.
                                </TableCell>
                            </TableRow>
                        ) : (
                            fields.map((field, index) => (
                                <TableRow key={index}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <span>{field.order + 1}</span>
                                            <Box sx={{ mr: 1 }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleMoveUp(index)}
                                                    disabled={index === 0}
                                                >
                                                    <ArrowUpIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleMoveDown(index)}
                                                    disabled={index === fields.length - 1}
                                                >
                                                    <ArrowDownIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>{field.name}</TableCell>
                                    <TableCell>{field.label}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={fieldTypes.find(ft => ft.value === field.type)?.label || field.type}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                        {field.options?.isMultiSelect && (
                                            <Chip
                                                label="چندتایی"
                                                size="small"
                                                color="secondary"
                                                variant="outlined"
                                                sx={{ mr: 0.5 }}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell>{field.required ? 'بله' : 'خیر'}</TableCell>
                                    <TableCell>{field.description || '-'}</TableCell>
                                    <TableCell>
                                        <Tooltip title="ویرایش فیلد">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEditField(index)}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="حذف فیلد">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDeleteField(index)}
                                                color="error"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Field Edit Dialog */}
            <ResponsiveModal
                open={dialogOpen}
                onClose={handleCloseDialog}
                maxWidth="md"
                title={dialogMode === 'create' ? 'افزودن فیلد جدید' : 'ویرایش فیلد'}
                actions={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <Button
                            variant="text"
                            color="secondary"
                            startIcon={<BookmarkAddIcon />}
                            onClick={handleOpenSaveAsTemplate}
                            disabled={!currentField?.name || !currentField?.label}
                        >
                            ذخیره به‌عنوان قالب
                        </Button>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button onClick={handleCloseDialog}>انصراف</Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSaveField}
                                disabled={!currentField?.name || !currentField?.label}
                            >
                                ذخیره
                            </Button>
                        </Box>
                    </Box>
                }
            >
                    <Box dir="rtl">
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="نام فیلد"
                                    name="name"
                                    value={currentField?.name || ''}
                                    onChange={handleFieldChange}
                                    required
                                    helperText="توجه: فقط حروف انگلیسی، اعداد و زیرخط (_) مجاز است"
                                    FormHelperTextProps={{ 
                                        style: { color: '#d32f2f', fontWeight: 'bold' } 
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    placeholder="skin_type"
                                    InputProps={{
                                        endAdornment: (
                                            <Button 
                                                size="small" 
                                                variant="text" 
                                                onClick={generateFieldName}
                                                disabled={!currentField?.label}
                                                sx={{ minWidth: 'auto', ml: -1.5 }}
                                            >
                                                تولید خودکار
                                            </Button>
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="عنوان نمایشی"
                                    name="label"
                                    value={currentField?.label || ''}
                                    onChange={handleFieldChange}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>نوع فیلد</InputLabel>
                                    <Select
                                        name="type"
                                        value={currentField?.type || 'text'}
                                        onChange={handleFieldChange}
                                        label="نوع فیلد"
                                    >
                                        {fieldTypes.map(type => (
                                            <MenuItem key={type.value} value={type.value}>
                                                {type.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="متن راهنما"
                                    name="placeholder"
                                    value={currentField?.placeholder || ''}
                                    onChange={handleFieldChange}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="توضیحات"
                                    name="description"
                                    value={currentField?.description || ''}
                                    onChange={handleFieldChange}
                                    multiline
                                    rows={2}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={currentField?.required || false}
                                            onChange={handleFieldChange}
                                            name="required"
                                        />
                                    }
                                    label="اجباری"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={currentField?.clientOnly || false}
                                            onChange={(e) => setCurrentField({ ...currentField!, clientOnly: e.target.checked })}
                                            name="clientOnly"
                                        />
                                    }
                                    label="فقط در کلاینت نمایش داده شود (عدم نمایش در سایر پنل‌ها)"
                                />
                            </Grid>

                            {/* ─── Conditional Visibility (نمایش شرطی) ─── */}
                            <Grid item xs={12}>
                                <Divider sx={{ my: 1 }} />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={!!currentField?.visibility}
                                            onChange={(e) => handleToggleVisibility(e.target.checked)}
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>نمایش شرطی</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                این فیلد فقط زمانی نمایش داده می‌شود که شرط(های) زیر برقرار باشد.
                                            </Typography>
                                        </Box>
                                    }
                                />
                                {currentField?.visibility && (
                                    <Card variant="outlined" sx={{ mt: 1.5, bgcolor: 'rgba(0,122,255,0.03)' }}>
                                        <CardContent>
                                            {currentField.visibility.conditions.length > 1 && (
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>ترکیب شرط‌ها:</Typography>
                                                    <FormControl size="small">
                                                        <Select
                                                            value={currentField.visibility.logic || 'AND'}
                                                            onChange={(e) => handleVisibilityLogicChange(e.target.value as 'AND' | 'OR')}
                                                            sx={{ minWidth: 200 }}
                                                        >
                                                            <MenuItem value="AND">همه شرط‌ها برقرار باشند (AND)</MenuItem>
                                                            <MenuItem value="OR">حداقل یکی از شرط‌ها برقرار باشد (OR)</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Box>
                                            )}

                                            {currentField.visibility.conditions.map((cond, idx) => {
                                                const otherFields = fields.filter(f => f.name !== currentField.name);
                                                const triggerField = otherFields.find(f => f.name === cond.fieldName);
                                                const operatorNeedsValue = !['truthy', 'falsy'].includes(cond.operator);
                                                const operatorIsList = ['in', 'notIn'].includes(cond.operator);
                                                const triggerHasItems = !!triggerField?.options?.items?.length;

                                                return (
                                                    <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5, p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                                        <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
                                                            {/* Trigger field */}
                                                            <FormControl size="small" fullWidth>
                                                                <InputLabel>فیلد مرجع</InputLabel>
                                                                <Select
                                                                    label="فیلد مرجع"
                                                                    value={cond.fieldName}
                                                                    onChange={(e) => handleVisibilityConditionChange(idx, 'fieldName', e.target.value)}
                                                                >
                                                                    {otherFields.length === 0 && <MenuItem disabled value="">ابتدا فیلد دیگری بسازید</MenuItem>}
                                                                    {otherFields.map(f => (
                                                                        <MenuItem key={f.name} value={f.name}>{f.label} ({f.name})</MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>

                                                            {/* Operator */}
                                                            <FormControl size="small" fullWidth>
                                                                <InputLabel>عملگر</InputLabel>
                                                                <Select
                                                                    label="عملگر"
                                                                    value={cond.operator}
                                                                    onChange={(e) => handleVisibilityConditionChange(idx, 'operator', e.target.value)}
                                                                >
                                                                    <MenuItem value="truthy">انتخاب شده / مقدار دارد</MenuItem>
                                                                    <MenuItem value="falsy">انتخاب نشده / خالی</MenuItem>
                                                                    <MenuItem value="equals">مساوی است با</MenuItem>
                                                                    <MenuItem value="notEquals">نامساوی است با</MenuItem>
                                                                    <MenuItem value="in">یکی از مقادیر</MenuItem>
                                                                    <MenuItem value="notIn">هیچ‌کدام از مقادیر</MenuItem>
                                                                    <MenuItem value="gt">بزرگ‌تر از</MenuItem>
                                                                    <MenuItem value="lt">کوچک‌تر از</MenuItem>
                                                                    <MenuItem value="contains">شامل باشد</MenuItem>
                                                                </Select>
                                                            </FormControl>

                                                            {/* Value */}
                                                            {operatorNeedsValue && (
                                                                triggerHasItems && !operatorIsList ? (
                                                                    <FormControl size="small" fullWidth>
                                                                        <InputLabel>مقدار</InputLabel>
                                                                        <Select
                                                                            label="مقدار"
                                                                            value={cond.value ?? ''}
                                                                            onChange={(e) => handleVisibilityConditionChange(idx, 'value', e.target.value)}
                                                                        >
                                                                            {triggerField!.options!.items!.map(opt => (
                                                                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                                                            ))}
                                                                        </Select>
                                                                    </FormControl>
                                                                ) : triggerHasItems && operatorIsList ? (
                                                                    <FormControl size="small" fullWidth>
                                                                        <InputLabel>مقادیر</InputLabel>
                                                                        <Select
                                                                            multiple
                                                                            label="مقادیر"
                                                                            value={Array.isArray(cond.value) ? cond.value : []}
                                                                            onChange={(e) => handleVisibilityConditionChange(idx, 'value', e.target.value)}
                                                                            renderValue={(sel) => (sel as string[]).join('، ')}
                                                                        >
                                                                            {triggerField!.options!.items!.map(opt => (
                                                                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                                                            ))}
                                                                        </Select>
                                                                    </FormControl>
                                                                ) : (
                                                                    <TextField
                                                                        size="small"
                                                                        fullWidth
                                                                        label={operatorIsList ? 'مقادیر (با , جدا کنید)' : 'مقدار'}
                                                                        value={Array.isArray(cond.value) ? cond.value.join(',') : (cond.value ?? '')}
                                                                        onChange={(e) => handleVisibilityConditionChange(
                                                                            idx,
                                                                            'value',
                                                                            operatorIsList ? e.target.value.split(',').map(s => s.trim()).filter(Boolean) : e.target.value
                                                                        )}
                                                                    />
                                                                )
                                                            )}
                                                        </Box>
                                                        <IconButton size="small" color="error" onClick={() => handleRemoveVisibilityCondition(idx)} sx={{ mt: 0.5 }}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                );
                                            })}

                                            <Button size="small" startIcon={<AddIcon />} onClick={handleAddVisibilityCondition}>
                                                افزودن شرط
                                            </Button>
                                        </CardContent>
                                    </Card>
                                )}
                            </Grid>

                            {/* Dropdown/Radio specific options */}
                            {currentField?.type === 'dropdown' || currentField?.type === 'radio' ? (
                                <>
                                    <Grid item xs={12}>
                                        <Divider sx={{ my: 1 }} />
                                        <Typography variant="subtitle1" gutterBottom>
                                            {currentField.type === 'dropdown' ? 'گزینه‌های لیست کشویی' : 'گزینه‌های دکمه رادیویی'}
                                        </Typography>

                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={currentField.options?.isStatic !== false}
                                                            onChange={handleFieldChange}
                                                            name="isStatic"
                                                        />
                                                    }
                                                    label="گزینه‌های ثابت"
                                                />
                                            </Grid>

                                            <Grid item>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={currentField.options?.isMultiSelect || false}
                                                            onChange={handleFieldChange}
                                                            name="isMultiSelect"
                                                        />
                                                    }
                                                    label="انتخاب چندتایی (Multiselect)"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    {/* Static options */}
                                    {currentField.options?.isStatic !== false ? (
                                        <Grid item xs={12}>
                                            <Box sx={{ mb: 1 }}>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<AddIcon />}
                                                    onClick={handleAddOption}
                                                >
                                                    افزودن گزینه
                                                </Button>
                                            </Box>
                                            <TableContainer component={Paper} variant="outlined">
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>عنوان</TableCell>
                                                            <TableCell>مقدار</TableCell>
                                                            <TableCell width="10%">عملیات</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {currentField.options?.items && currentField.options.items.length > 0 ? (
                                                            currentField.options.items.map((option, index) => (
                                                                <TableRow key={index}>
                                                                    <TableCell>
                                                                        <TextField
                                                                            fullWidth
                                                                            size="small"
                                                                            value={option.label}
                                                                            onChange={(e) => handleOptionChange(index, 'label', e.target.value)}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <TextField
                                                                            fullWidth
                                                                            size="small"
                                                                            value={option.value}
                                                                            onChange={(e) => handleOptionChange(index, 'value', e.target.value)}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleDeleteOption(index)}
                                                                            color="error"
                                                                        >
                                                                            <DeleteIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))
                                                        ) : (
                                                            <TableRow>
                                                                <TableCell colSpan={3} align="center">
                                                                    هیچ گزینه‌ای تعریف نشده است.
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Grid>
                                    ) : (
                                        // Dynamic source options
                                        <Grid item xs={12}>
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="subtitle2" gutterBottom>
                                                        تنظیمات منبع پویا
                                                    </Typography>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} md={12} lg={4}>
                                                            <FormControl fullWidth size="small">
                                                                <InputLabel>کالکشن</InputLabel>
                                                                <Select
                                                                    value={currentField.options?.dynamicSource?.collectionName || ''}
                                                                    onChange={(e) => handleDynamicSourceChange('collectionName', e.target.value as string)}
                                                                    label="کالکشن"
                                                                    disabled={loadingCollections}
                                                                    endAdornment={loadingCollections && <CircularProgress size={20} sx={{ mr: 1 }} />}
                                                                >
                                                                    {availableCollections.map(collection => (
                                                                        <MenuItem key={collection.id} value={collection.id}>
                                                                            {collection.name}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        </Grid>

                                                        <Grid item xs={12} md={12} lg={4}>
                                                            <FormControl fullWidth size="small" disabled={loadingFields || !collectionFields.length}>
                                                                <InputLabel>فیلد نمایشی</InputLabel>
                                                                <Select
                                                                    value={currentField.options?.dynamicSource?.displayField || ''}
                                                                    onChange={(e) => handleDynamicSourceChange('displayField', e.target.value as string)}
                                                                    label="فیلد نمایشی"
                                                                    endAdornment={loadingFields && <CircularProgress size={20} sx={{ mr: 1 }} />}
                                                                >
                                                                    {collectionFields.map(field => (
                                                                        <MenuItem key={field.id} value={field.id}>
                                                                            {field.name} ({field.id})
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                                <Typography variant="caption" color="textSecondary">
                                                                    فیلدی که به کاربر نمایش داده می‌شود
                                                                </Typography>
                                                            </FormControl>
                                                        </Grid>

                                                        <Grid item xs={12} md={12} lg={4}>
                                                            <FormControl fullWidth size="small" disabled={loadingFields || !collectionFields.length}>
                                                                <InputLabel>فیلد مقدار</InputLabel>
                                                                <Select
                                                                    value={currentField.options?.dynamicSource?.valueField || ''}
                                                                    onChange={(e) => handleDynamicSourceChange('valueField', e.target.value as string)}
                                                                    label="فیلد مقدار"
                                                                    endAdornment={loadingFields && <CircularProgress size={20} sx={{ mr: 1 }} />}
                                                                >
                                                                    {collectionFields.map(field => (
                                                                        <MenuItem key={field.id} value={field.id}>
                                                                            {field.name} ({field.id})
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                                <Typography variant="caption" color="textSecondary">
                                                                    فیلدی که به عنوان مقدار استفاده می‌شود
                                                                </Typography>
                                                            </FormControl>
                                                        </Grid>

                                                        {currentField.options?.dynamicSource?.collectionName && (
                                                            <Grid item xs={12} display="flex" justifyContent="center" mt={1}>
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    startIcon={<RefreshIcon />}
                                                                    onClick={() => {
                                                                        if (currentField.options?.dynamicSource?.collectionName) {
                                                                            loadCollectionFields(currentField.options?.dynamicSource?.collectionName!);
                                                                        }
                                                                    }}
                                                                    disabled={loadingFields}
                                                                >
                                                                    بارگذاری مجدد فیلدها
                                                                </Button>
                                                            </Grid>
                                                        )}
                                                    </Grid>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    )}
                                </>
                            ) : null}

                            {/* Price field specific options */}
                            {currentField?.type === 'price' ? (
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="subtitle1" gutterBottom>
                                        ماتریس قیمت
                                    </Typography>
                                    <Box sx={{ mb: 1 }}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<AddIcon />}
                                            onClick={handleAddPriceOption}
                                        >
                                            افزودن گزینه قیمت
                                        </Button>
                                    </Box>
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>حجم / سایز</TableCell>
                                                    <TableCell>قیمت</TableCell>
                                                    <TableCell>توضیحات</TableCell>
                                                    <TableCell width="10%">عملیات</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {currentField.options?.priceMatrix && currentField.options.priceMatrix.length > 0 ? (
                                                    currentField.options.priceMatrix.map((option, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    type="number"
                                                                    value={option.capacity}
                                                                    onChange={(e) => handlePriceOptionChange(index, 'capacity', e.target.value)}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    type="number"
                                                                    value={option.price}
                                                                    onChange={(e) => handlePriceOptionChange(index, 'price', e.target.value)}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    value={option.description || ''}
                                                                    onChange={(e) => handlePriceOptionChange(index, 'description', e.target.value)}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleDeletePriceOption(index)}
                                                                    color="error"
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={4} align="center">
                                                            هیچ گزینه قیمتی تعریف نشده است.
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Grid>
                            ) : null}

                            {/* Capacity Type field specific options */}
                            {currentField?.type === 'capacity_type' ? (
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="subtitle1" gutterBottom>
                                        گزینه‌های حجم / سایز
                                    </Typography>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={Boolean(currentField.options?.perCapacityInventory)}
                                                onChange={(e) => {
                                                    setCurrentField({
                                                        ...currentField!,
                                                        options: {
                                                            ...currentField!.options,
                                                            perCapacityInventory: e.target.checked
                                                        }
                                                    });
                                                }}
                                                name="perCapacityInventory"
                                            />
                                        }
                                        label="موجودی جدا برای هر حجم / سایز"
                                        sx={{ mb: 1 }}
                                    />
                                    <Box sx={{ mb: 1 }}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<AddIcon />}
                                            onClick={handleAddCapacityOption}
                                        >
                                            افزودن حجم / سایز
                                        </Button>
                                    </Box>
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>نام</TableCell>
                                                    <TableCell>توضیحات</TableCell>
                                                    <TableCell>مقدار</TableCell>
                                                    <TableCell width="10%">عملیات</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {currentField.options?.capacityTypes && currentField.options.capacityTypes.length > 0 ? (
                                                    currentField.options.capacityTypes.map((option, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    value={option.name}
                                                                    onChange={(e) => handleCapacityOptionChange(index, 'name', e.target.value)}
                                                                    placeholder="مثلاً ۵۰ میلی‌لیتر"
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    value={option.description || ''}
                                                                    onChange={(e) => handleCapacityOptionChange(index, 'description', e.target.value)}
                                                                    placeholder="توضیحات..."
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    value={option.value || ''}
                                                                    onChange={(e) => handleCapacityOptionChange(index, 'value', e.target.value)}
                                                                    placeholder="مقدار..."
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleDeleteCapacityOption(index)}
                                                                    color="error"
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={4} align="center">
                                                            هیچ گزینهٔ حجم / سایزی تعریف نشده است.
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Grid>
                            ) : null}


                            {/* ─── Sub-form (فرم تو در تو) ─── */}
                            <Grid item xs={12}>
                                <Divider sx={{ my: 1 }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                    <Box>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>فیلدهای داخلی (فرم تو در تو)</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            می‌توانید یک فرم کامل با تمام امکانات بالا داخل این فیلد بسازید. به‌صورت پیش‌فرض زمانی که این فیلد مقدار داشته باشد نمایش داده می‌شوند؛ برای کنترل دقیق‌تر، روی هر زیرفیلد «نمایش شرطی» را تنظیم کنید.
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={{
                                    p: 1.5,
                                    bgcolor: 'rgba(0,122,255,0.04)',
                                    border: '1px dashed',
                                    borderColor: 'rgba(0,122,255,0.3)',
                                    borderRadius: 1
                                }}>
                                    <FieldEditor
                                        fields={currentField?.subFields || []}
                                        onChange={(subFields) => {
                                            if (!currentField) return;
                                            setCurrentField({ ...currentField, subFields });
                                        }}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
            </ResponsiveModal>

            {/* ─── Template Picker Dialog ──────────────────────────────── */}
            <ResponsiveModal
                open={templatePickerOpen}
                onClose={() => setTemplatePickerOpen(false)}
                maxWidth="md"
                title="افزودن فیلد از قالب"
                actions={<Button onClick={() => setTemplatePickerOpen(false)}>بستن</Button>}
            >
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, mt: 1 }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="جستجو در نام/توضیحات..."
                            value={templateSearch}
                            onChange={(e) => setTemplateSearch(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') loadTemplates(); }}
                            InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ ml: 0.5, color: 'text.disabled' }} /> }}
                        />
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>دسته‌بندی</InputLabel>
                            <Select
                                label="دسته‌بندی"
                                value={templateCategoryFilter}
                                onChange={(e) => { setTemplateCategoryFilter(e.target.value as string); }}
                            >
                                <MenuItem value="">همه</MenuItem>
                                {templateCategories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <Button variant="outlined" onClick={loadTemplates} disabled={templatesLoading}>
                            {templatesLoading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                        </Button>
                    </Box>

                    {templatesLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : templates.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                            هیچ قالبی یافت نشد. در دیالوگ ویرایش فیلد روی «ذخیره به‌عنوان قالب» کلیک کنید تا فیلد فعلی به‌عنوان قالب ذخیره شود.
                        </Box>
                    ) : (
                        <Box sx={{ display: 'grid', gap: 1 }}>
                            {templates.map(tpl => (
                                <Paper
                                    key={tpl._id}
                                    variant="outlined"
                                    sx={{
                                        p: 1.5,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        transition: 'all 0.15s',
                                        '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' },
                                    }}
                                    onClick={() => handleInsertTemplate(tpl)}
                                >
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{tpl.name}</Typography>
                                            <Chip
                                                label={(fieldTypes.find(ft => ft.value === (tpl.fieldDefinition as any)?.type)?.label) || (tpl.fieldDefinition as any)?.type}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                                sx={{ height: 20, fontSize: '0.7rem' }}
                                            />
                                            {tpl.category && (
                                                <Chip label={tpl.category} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                                            )}
                                        </Box>
                                        {tpl.description && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                {tpl.description}
                                            </Typography>
                                        )}
                                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }}>
                                            {tpl.usageCount > 0 ? `${tpl.usageCount} بار استفاده شده` : 'هنوز استفاده نشده'}
                                        </Typography>
                                    </Box>
                                    <Button size="small" variant="contained" startIcon={<AddIcon />}>افزودن</Button>
                                </Paper>
                            ))}
                        </Box>
                    )}
            </ResponsiveModal>

            {/* ─── Save As Template Dialog ────────────────────────────────── */}
            <ResponsiveModal
                open={saveAsTemplateOpen}
                onClose={() => setSaveAsTemplateOpen(false)}
                maxWidth="sm"
                title="ذخیره فیلد به‌عنوان قالب"
                disableClose={templateSaving}
                actions={
                    <>
                        <Button onClick={() => setSaveAsTemplateOpen(false)} disabled={templateSaving}>انصراف</Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSaveAsTemplate}
                            disabled={templateSaving || !templateSaveName.trim()}
                            startIcon={templateSaving ? <CircularProgress size={14} /> : <BookmarkAddIcon />}
                        >
                            ذخیره قالب
                        </Button>
                    </>
                }
            >
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        این فیلد به‌عنوان یک قالب قابل استفاده مجدد در سایر انواع محصول ذخیره می‌شود.
                        تغییرات بعدی روی این فیلد روی قالب اثر نمی‌گذارد (و بالعکس).
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label="نام قالب"
                                value={templateSaveName}
                                onChange={(e) => setTemplateSaveName(e.target.value)}
                                required
                                helperText="نام منحصربه‌فرد برای شناسایی این قالب"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label="دسته‌بندی (اختیاری)"
                                value={templateSaveCategory}
                                onChange={(e) => setTemplateSaveCategory(e.target.value)}
                                helperText="مثلاً: مراقبت پوست، آرایشی، ارسال"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label="توضیحات (اختیاری)"
                                value={templateSaveDescription}
                                onChange={(e) => setTemplateSaveDescription(e.target.value)}
                                multiline
                                rows={2}
                            />
                        </Grid>
                    </Grid>
            </ResponsiveModal>
        </Box>
    );
};

export default FieldEditor; 